import type { ServiceSlug } from "./site";

export type BlogCategory =
  | "Activaciones BTL"
  | "Eventos Corporativos"
  | "Trade Marketing"
  | "Marketing Promocional"
  | "Guías y Consejos";

export interface BlogPost {
  slug: string;
  title: string;
  h1: string;
  excerpt: string;
  metaDescription: string;
  category: BlogCategory;
  primaryKeyword: string;
  secondaryKeywords: string[];
  intent: "informativa" | "comercial" | "comparativa" | "transaccional";
  serviceLinks: ServiceSlug[];
  relatedPosts?: string[];
  ctaLabel: string;
  /** "/postular" marca un post de empleo (para quien busca trabajo, no para la marca). */
  ctaHref: "/cotizar" | "/postular" | ServiceSlug;
  publishedAt: string;
  updatedAt?: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageCaption?: string;
  readMinutes: number;
  contentMarkdown: string;
  faqs?: { q: string; a: string }[];
}

/**
 * Post dirigido a quien busca trabajo (sueldos, cómo postular): su CTA lleva a
 * /postular. La plantilla no le muestra el cierre comercial («Solicitar
 * propuesta»): mezclar intención de empleo y de compra en la misma página
 * confunde al lector y a Google sobre a quién sirve.
 */
export function esPostDeEmpleo(post: Pick<BlogPost, "ctaHref">): boolean {
  return post.ctaHref === "/postular";
}

export const BLOG_CATEGORIES: BlogCategory[] = [
  "Activaciones BTL",
  "Eventos Corporativos",
  "Trade Marketing",
  "Marketing Promocional",
  "Guías y Consejos",
];

export const CATEGORY_SLUGS: Record<BlogCategory, string> = {
  "Activaciones BTL": "activaciones-btl",
  "Eventos Corporativos": "eventos-corporativos",
  "Trade Marketing": "trade-marketing",
  "Marketing Promocional": "marketing-promocional",
  "Guías y Consejos": "guias-y-consejos",
};

/** Guías de una categoría agrupadas por la decisión que resuelven. Se muestran solo las publicadas. */
export interface GrupoGuias {
  titulo: string;
  slugs: string[];
}

export interface CategoryMeta {
  h1: string;
  title: string;
  description: string;
  intro: string;
  /**
   * Texto editorial propio de la categoría (2 párrafos). Sin él, la página es solo una lista de
   * tarjetas con contenido casi idéntico al del índice del blog, y Google la rastrea sin indexarla.
   */
  texto: string[];
  /** Guías destacadas, agrupadas; cada slug debe ser un post publicado de ESTA categoría. */
  grupos: GrupoGuias[];
}

export const CATEGORY_META: Record<BlogCategory, CategoryMeta> = {
  "Activaciones BTL": {
    h1: "Activaciones BTL en Lima: guías y recursos",
    title: "Activaciones BTL en Lima: Guías | Improve Yourself",
    description:
      "Activaciones BTL: qué son, cuánto cuestan, cómo elegir agencia y qué estrategias funcionan para marcas en Lima. Guías prácticas.",
    intro:
      "Guías sobre qué es una activación BTL, cuánto cuesta implementarla, cómo seleccionar una agencia y qué tácticas generan resultados reales en Lima.",
    texto: [
      "Una activación BTL es una acción de marca en contacto directo con el público: en la calle, en el punto de venta o dentro de un evento. A diferencia de la publicidad en medios, busca que la persona pruebe, pregunte o participe, y por eso se evalúa con métricas de contacto y de conversión, no de alcance.",
      "Las guías de esta categoría siguen el orden en que se toman las decisiones: qué es una activación y qué formato conviene, cuánto cuesta y cómo se desglosa el presupuesto, cómo ejecutar en calle o en tienda y cómo adaptarla a un objetivo o a un sector. Si es tu primera campaña, empieza por la definición; si ya tienes presupuesto aprobado, ve directo a formatos y costos.",
    ],
    grupos: [
      {
        titulo: "Para empezar",
        slugs: [
          "que-es-una-activacion-btl",
          "activaciones-en-lima-guia-para-elegir-el-formato-ideal",
          "costo-campana-btl-en-peru-rangos-reales-y-desglose",
        ],
      },
      {
        titulo: "Formatos en la calle y en tienda",
        slugs: [
          "street-marketing-lima",
          "batucada-en-activaciones-de-marca-guia-completa",
          "ideas-activaciones-btl-retail",
        ],
      },
      {
        titulo: "Por objetivo o por sector",
        slugs: [
          "activaciones-btl-lanzamiento-de-producto",
          "activaciones-btl-sector-alimentos-bebidas",
          "activaciones-btl-sector-financiero",
          "activaciones-btl-cosmeticos-belleza",
        ],
      },
    ],
  },
  "Eventos Corporativos": {
    h1: "Eventos corporativos en Lima: guías y recursos",
    title: "Eventos Corporativos Lima: Guías | Improve Yourself",
    description:
      "Producción de eventos corporativos en Lima: lanzamientos, kick-offs y convenciones. Guías para áreas de marketing.",
    intro:
      "Recursos prácticos para planificar y producir eventos corporativos en Lima: lanzamientos de marca, kick-offs, convenciones y reuniones con ejecución ordenada.",
    texto: [
      "Un evento corporativo bien producido combina objetivo, programa, proveedores y ejecución en sitio. Aquí encontrarás guías para planificar lanzamientos, kick-offs y convenciones en Lima, estimar el presupuesto, elegir a quién contratar y decidir los formatos de integración y animación que mejor encajan con tu equipo.",
      "Si todavía estás definiendo el tipo de evento, empieza por las guías de planificación. Si ya tienes fecha y presupuesto, las de proveedores te ayudan a comparar propuestas con criterio: qué debe incluir una cotización, qué preguntar a una productora y cuándo conviene un solo responsable para todo el evento.",
    ],
    grupos: [
      {
        titulo: "Planificar el evento",
        slugs: [
          "como-organizar-un-evento-corporativo-exitoso",
          "evento-corporativo-en-lima-que-es-tipos-y-como-producirlo",
          "cuanto-cuesta-producir-un-evento-corporativo-en-lima-2025",
          "como-organizar-kickoff-ventas",
          "como-organizar-una-convencion-empresarial-exitosa-en-lima",
          "como-planificar-lanzamiento-marca",
        ],
      },
      {
        titulo: "Elegir proveedor",
        slugs: [
          "produccion-de-eventos-en-lima-que-incluye-y-como-elegir-proveedor",
          "que-hace-una-empresa-de-eventos-corporativos",
          "catering-para-empresas-todo-lo-que-debes-saber",
        ],
      },
      {
        titulo: "Formatos, integración y animación",
        slugs: [
          "ideas-eventos-corporativos-creativos",
          "team-building-lima",
          "ideas-de-gymkana-para-eventos-corporativos-en-lima",
          "hora-loca-para-eventos-corporativos-guia-completa",
          "show-navideno-para-empresas-lima-guia-completa",
        ],
      },
    ],
  },
  "Trade Marketing": {
    h1: "Trade marketing en Lima: guías y recursos",
    title: "Trade Marketing en Lima: Guías | Improve Yourself",
    description:
      "Trade marketing en canal moderno y tradicional: material POP, exhibiciones y campañas en punto de venta. Guías para Lima.",
    intro:
      "Todo sobre trade marketing en canal moderno y tradicional: material POP, exhibiciones, campañas en punto de venta y estrategias para mejorar presencia en retail.",
    texto: [
      "El trade marketing trabaja donde se decide la compra: el supermercado, la farmacia, la bodega. Combina material de punto de venta, exhibiciones, activación de personal y negociación con el canal para que la marca esté visible, disponible y bien presentada frente al comprador.",
      "Esta categoría parte de los fundamentos (qué es y cómo funciona en canal moderno y tradicional), sigue con las herramientas del punto de venta —exhibiciones, material POP y merchandising— y termina con la ejecución en tienda: sampling, degustaciones y el personal que las atiende.",
    ],
    grupos: [
      {
        titulo: "Fundamentos",
        slugs: [
          "que-es-trade-marketing",
          "trade-marketing-supermercados-peru",
          "cuanto-cuesta-trade-marketing-lima",
        ],
      },
      {
        titulo: "Herramientas en el punto de venta",
        slugs: [
          "exhibiciones-punto-de-venta",
          "que-es-material-pop",
          "tipos-de-merchandising-que-impulsan-tus-ventas",
        ],
      },
      {
        titulo: "Sampling y personal en tienda",
        slugs: [
          "sampling-en-punto-de-venta-como-ejecutarlo-paso-a-paso",
          "cuanto-cobra-una-impulsadora-en-lima-para-punto-de-venta",
        ],
      },
    ],
  },
  "Marketing Promocional": {
    h1: "Marketing promocional en Lima: guías y recursos",
    title: "Marketing Promocional Lima: Guías | Improve Yourself",
    description:
      "Impulsación, sampling, degustaciones y volanteo publicitario. Recursos para marcas que buscan presencia directa en Lima.",
    intro:
      "Recursos sobre impulsación con promotoras, campañas de sampling, degustaciones y volanteo publicitario para marcas que quieren presencia directa en Lima.",
    texto: [
      "El marketing promocional pone el producto en manos del consumidor: sampling, degustaciones, volanteo e impulsación. Su fortaleza es la prueba directa, que reduce el riesgo percibido de comprar algo nuevo y acorta el camino entre conocer una marca y comprarla.",
      "Las guías explican cómo se planifica una campaña de sampling, cómo se ejecuta y se mide una degustación en supermercado y cuándo el volanteo publicitario sí funciona. Para el detalle de la operación en tienda, mira también la categoría de trade marketing.",
    ],
    grupos: [
      {
        titulo: "Sampling, degustaciones y volanteo",
        slugs: ["degustaciones-supermercados", "que-es-volanteo-publicitario-cuando-funciona"],
      },
    ],
  },
  "Guías y Consejos": {
    h1: "Guías BTL y consejos para marketing en Lima",
    title: "Guías BTL en Lima: Consejos | Improve Yourself",
    description:
      "Guías paso a paso para planificar, ejecutar y medir activaciones BTL, eventos y campañas de marketing directo en Lima.",
    intro:
      "Guías prácticas para planificar, ejecutar y medir activaciones BTL, eventos corporativos y campañas de marketing directo en Lima.",
    texto: [
      "Esta categoría reúne las guías transversales sobre BTL: cómo decidir entre canales, cómo elegir y contratar una agencia, cómo presupuestar, qué personal necesitas y cómo medir lo que ejecutas. Son temas que se repiten en casi cualquier campaña, sin importar el sector.",
      "Están ordenadas por la decisión que suele enfrentar un equipo de marketing, de la más temprana (qué canal usar) a la más tardía (cómo saber si funcionó). Si buscas guías por formato o por tipo de evento, mira las categorías de activaciones BTL y de eventos corporativos.",
    ],
    grupos: [
      {
        titulo: "Entender el BTL y elegir canal",
        slugs: [
          "marketing-btl-en-peru-que-es-formatos-y-como-aplicarlo",
          "btl-vs-atl-cuando-usar-cada-uno",
          "btl-vs-publicidad-digital-en-peru-cuando-elegir-cada-estrategia",
          "activaciones-presenciales-o-redes-sociales-cual-elegir",
          "activaciones-btl-vs-trade-marketing",
        ],
      },
      {
        titulo: "Contratar agencia y presupuestar",
        slugs: [
          "todo-lo-que-hace-una-agencia-btl-en-lima-por-tu-marca",
          "empresa-btl-peru-que-buscar-antes-de-contratar",
          "como-estructurar-presupuesto-btl-en-peru-paso-a-paso",
          "como-presupuestar-campana-btl",
          "errores-activaciones-btl",
        ],
      },
      {
        titulo: "Medir resultados",
        slugs: ["como-medir-activaciones-btl", "como-calcular-roi-activaciones-btl"],
      },
      {
        titulo: "Personal para eventos y activaciones",
        slugs: [
          "anfitrionas-promotoras-e-impulsadoras-cual-necesitas",
          "impulsadoras-en-lima-todo-lo-que-debes-saber-antes-de-contratar",
          "perfil-ideal-de-promotora-de-consumo-masivo",
          "animadores-en-lima-como-contratar-al-perfil-correcto",
          "promotoras-de-ventas-funciones-sueldos-y-como-postular",
          "que-es-el-cotillon-y-como-usarlo-en-eventos",
        ],
      },
    ],
  },
};

export function getCategoryBySlug(slug: string): BlogCategory | undefined {
  return (Object.entries(CATEGORY_SLUGS) as [BlogCategory, string][]).find(
    ([, s]) => s === slug,
  )?.[0];
}
